<template>
  <v-card class="d-flex flex-column">
    <v-toolbar dark color="primary" style="z-index: 2;" class="pa-0 ma-0 pt-1">
      <v-btn icon dark @click="$emit('closeDialog')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>
        {{ title }}
        <v-divider vertical class="ml-4 mr-4"> </v-divider>

        <v-tooltip bottom color="primary">
          <template v-slot:activator="{ on: tooltip }">
            <span v-on="{ ...tooltip }">
              <v-chip label color="white" class="primary--text title mr-2">{{
                analysis.cohorts.length
              }}</v-chip>
              <span class="white--text text-body-1"
                >Study group{{ analysis.cohorts.length == 1 ? '' : 's' }}</span
              >
            </span>
          </template>
          <span class="subtitle-1">
            {{
              analysis.cohorts
                .map(c => c.label.replace(/^\[O\]_/, ''))
                .join(', ')
            }}
          </span>
        </v-tooltip>

        <v-divider vertical class="ml-4 mr-4"> </v-divider>

        <v-tooltip bottom color="primary">
          <template v-slot:activator="{ on: tooltip }">
            <span v-on="{ ...tooltip }">
              <v-chip label color="white" class="primary--text title mr-2">{{
                analysis.outcomeVariables.length
              }}</v-chip>
              <span class="white--text text-body-1"
                >Variable{{
                  analysis.outcomeVariables.length == 1 ? '' : 's'
                }}</span
              >
            </span>
          </template>
          <span class="subtitle-1">
            {{
              analysis.outcomeVariables.map(ov => ov.abbreviation).join(', ')
            }}
          </span>
        </v-tooltip>

        <v-divider vertical class="ml-4 mr-4"> </v-divider>

        <v-tooltip bottom color="primary">
          <template v-slot:activator="{ on: tooltip }">
            <span v-on="{ ...tooltip }">
              <span class="white--text text-body-1">Analysis Type: </span>
              <v-chip label color="white" class="primary--text title mr-2">{{
                selectedAnalysisMode
              }}</v-chip>
            </span>
          </template>
          <span class="subtitle-1"> {{ selectedAnalysisMode }} Mode </span>
        </v-tooltip>

        <v-divider
          v-if="analysis.collection.is_longitudinal"
          vertical
          class="ml-4 mr-4"
        >
        </v-divider>

        <v-tooltip
          v-if="analysis.collection.is_longitudinal"
          bottom
          color="primary"
        >
          <template v-slot:activator="{ on: tooltip }">
            <span v-on="{ ...tooltip }">
              <span class="white--text text-body-1">Comparing: </span>
              <v-chip label color="white" class="primary--text title mr-2">{{
                analysis.input.comparisonFieldDescr
              }}</v-chip>
            </span>
          </template>
          <span class="subtitle-1">
            Comparing {{ analysis.input.comparisonFieldLongDescr }}
          </span>
        </v-tooltip>

        <v-divider vertical class="ml-4 mr-4"> </v-divider>

        <v-tooltip bottom color="primary">
          <template v-slot:activator="{ on: tooltip }">
            <span v-on="{ ...tooltip }">
              <span class="white--text text-body-1">Status: </span>
              <v-chip
                label
                :color="statusColor[analysis.status]"
                class="white--text title mr-2"
                >{{ analysis.status }}</v-chip
              >
            </span>
          </template>
          <span v-if="analysis.status == 'Failed'" class="subtitle-1">
            {{ analysis.error }}
          </span>
          <span v-else class="subtitle-1">
            {{ analysis.status }} at {{ analysis.statusTime | formatDate }}
          </span>
        </v-tooltip>

        <v-spacer />

        {{ getAnalysisDescription() }}
      </v-toolbar-title>
      <v-spacer />
    </v-toolbar>

    <splitpanes horizontal color="white" class="default-theme pt-2">
      <!-- 1st row -->
      <pane size="50" class="s_pane px-3">
        <splitpanes
          ref="splitter"
          color="white"
          class="default-theme"
          @resize="splitPane1Resized"
        >
          <pane size="45" class="pb-1 s_pane">
            <v-sheet color="white" class="rounded-lg shadow">
              <v-tabs v-model="leftTab1" light>
                <v-tab key="cohorts">
                  <span v-if="selectedAnalysisMode == 'Predictor based'"
                    >Study groups</span
                  >
                  <span v-else>Study groups</span>
                </v-tab>
                <v-tab key="analytics">
                  <span v-if="selectedAnalysisMode == 'Predictor based'"
                    >Outcome Variables/Analytics</span
                  >
                  <span v-else>Predictor Variables/Analytics</span>
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="leftTab1" class="pt-3">
                <v-tab-item key="cohorts" :eager="true">
                  <cohort-table
                    :cohorts="analysis.cohorts"
                    :select-cohorts="visibleCohorts"
                    :show-title-bar="false"
                    :expanded="expandAnalytics"
                    report-max-overlap
                    show-select
                    show-colors
                    @selectedCohorts="updateVisibleCohorts"
                    @cohortColorChange="updateCohortColor"
                  />
                </v-tab-item>
                <v-tab-item key="analytics" :eager="true">
                  <analytics-panel
                    ref="apanel"
                    :analysis="analysis"
                    :selected-variables="selectedVars"
                    :anova-pvals="analysis.pvals"
                    :anova-pvals-input="analysis.input"
                    :outcome-variables="analysis.outcomeVariables"
                    :show-title-bar="false"
                    :multi-select="multiSelect"
                    autoselect-first-variable
                    :expanded="expandAnalytics"
                    color-scheme="brewer5"
                    @variablesSelected="variablesSelected"
                    @expandClicked="analyticsExpandClicked"
                  />
                </v-tab-item>
              </v-tabs-items>
            </v-sheet>
          </pane>

          <pane size="65" class="pb-1 s_pane">
            <v-sheet color="white" min-height="400px" class="rounded-lg shadow">
              <v-tabs v-model="rightTab1" light>
                <v-tab
                  key="lview"
                  :disabled="!analysis.collection.is_longitudinal"
                  >Longitudinal Graphs</v-tab
                >
                <v-tab key="pairwise" :disabled="selectedVars.length != 1"
                  >Pairwise Comparisons</v-tab
                >
                <v-tab v-if="selectedAnalysisMode == 'Outcome based'">
                  Regression Analysis
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="rightTab1">
                <v-tab-item
                  key="lview"
                  :disabled="!analysis.collection.is_longitudinal"
                  :eager="true"
                >
                  <v-container fluid fill-width class="ma-0 pa-0">
                    <v-row
                      v-for="(detailedView, dvn) in selectedVars"
                      :key="`dv-${analysis.index}-${dvn}`"
                      class="ma-0 pa-0"
                    >
                      <v-col
                        v-if="
                          analysis.collection.is_longitudinal &&
                            selectedVarsContainsLongitudinal
                        "
                        cols="12"
                        class="ma-0 pa-0 export-content"
                      >
                        <v-container
                          style="padding: 0; margin-top: 0.5rem; margin-bottom: 0.5rem;"
                        >
                          <v-row class="d-flex justify-center align-center">
                            <!-- title -->
                            <v-col cols="auto" class="text-center pb-0 pt-2">
                              <v-container class="ma-0 pa-0">
                                <v-row class="ma-0 pa-0">
                                  <v-col cols="12" class="ma-0 pa-0">
                                    <v-card-title
                                      class="primary--text pa-0 pl-2"
                                    >
                                      <v-tooltip bottom color="primary">
                                        <template
                                          v-slot:activator="{ on: tooltip }"
                                        >
                                          <img
                                            :src="
                                              '/images/' +
                                                detailedView.category +
                                                '-icon-128.png'
                                            "
                                            :title="detailedView.category"
                                            style="height:1.25em"
                                            class="ma-1"
                                            @error="missingDomainFallback"
                                            v-on="{ ...tooltip }"
                                          />
                                        </template>
                                        <span>{{ detailedView.category }}</span>
                                      </v-tooltip>

                                      <v-tooltip
                                        v-if="detailedView"
                                        top
                                        color="primary"
                                      >
                                        <template
                                          v-slot:activator="{ on: tooltip }"
                                        >
                                          <span
                                            class="text-subtitle-1"
                                            v-on="{ ...tooltip }"
                                          >
                                            {{
                                              detailedView.abbreviation ===
                                              detailedView.label
                                                ? detailedView.abbreviation
                                                : detailedView.abbreviation +
                                                  ' | ' +
                                                  detailedView.label
                                            }}
                                          </span>
                                        </template>
                                        <span
                                          >{{ detailedView.description }}
                                        </span>
                                      </v-tooltip>

                                      <span v-else> {{ title }} </span>
                                      <v-spacer />
                                    </v-card-title>
                                  </v-col>
                                </v-row>
                              </v-container>
                            </v-col>
                            <!-- export -->
                            <v-col cols="auto" class="text-center pb-0 pt-2">
                              <export-interface
                                :analysis="analysis"
                                :variable="detailedView"
                                export-type="longitudinalGraph"
                              />
                            </v-col>
                          </v-row>
                        </v-container>

                        <detailed-view
                          ref="dview"
                          :analysis="analysis"
                          :cohorts="visibleCohorts"
                          :show-title-bar="false"
                          :outcome-var="detailedView"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-tab-item>

                <v-tab-item
                  key="pairwise"
                  :disabled="selectedVars.length != 1"
                  :eager="false"
                >
                  <tukey-hsd-heatmap
                    v-if="selectedVars.length == 1"
                    ref="tukeyhsd"
                    :analysis="analysis"
                    :cohorts="analysis.cohorts"
                    :visible-cohorts="visibleCohorts"
                    :outcome-var="selectedVars[0]"
                    :color-scheme="pairwiseColorScheme"
                    :show-title-bar="false"
                    :selected-analysis-mode="selectedAnalysisMode"
                    @selectedCohorts="updateVisibleCohorts"
                  />
                </v-tab-item>

                <v-tab-item
                  v-if="selectedAnalysisMode == 'Outcome based'"
                  key="regression"
                  :eager="false"
                >
                  <forest-plot
                    :analysis="analysis"
                    :visible-cohorts="visibleCohorts"
                    :selected-analysis-mode="selectedAnalysisMode"
                  ></forest-plot>
                </v-tab-item>
              </v-tabs-items>
            </v-sheet>
          </pane>
        </splitpanes>
      </pane>
      <!-- end 1st row -->

      <!-- 2nd row -->
      <pane size="50" class="s_pane px-3">
        <splitpanes
          ref="splitter"
          color="white"
          class="default-theme"
          @resize="splitPane2Resized"
        >
          <pane size="45" class="pb-1 s_pane">
            <v-sheet color="white" class="rounded-lg shadow">
              <v-tabs v-model="leftTab2" light>
                <v-tooltip top color="primary">
                  <template v-slot:activator="{ on: tooltip }">
                    <v-tab key="table1" v-on="{ ...tooltip }"
                      >Sample Characteristics</v-tab
                    >
                  </template>
                  <span class="text-body-1"
                    >Sample Characteristics (Table 1)</span
                  >
                </v-tooltip>

                <v-tooltip top color="primary">
                  <template v-slot:activator="{ on: tooltip }">
                    <v-tab key="table2" v-on="{ ...tooltip }"
                      >Summary of Results</v-tab
                    >
                  </template>
                  <span class="text-body-1">Summary of Results (Table 2)</span>
                </v-tooltip>
              </v-tabs>

              <v-tabs-items v-model="leftTab2" class="pt-3">
                <v-tab-item key="table1" :eager="true">
                  <summary-stats-table
                    :predictor-variables="analysis.predictorVariables"
                    :outcome-variables="analysis.outcomeVariables"
                    :selected-variables="selectedVars"
                    :cohorts="analysis.cohorts"
                    :show-category-icons="true"
                    :comparison-field="analysis.input.comparisonField"
                    :is-longitudinal="analysis.collection.is_longitudinal"
                    :analysis="analysis"
                    :selected-analysis-mode="selectedAnalysisMode"
                    display-mode="table1"
                    @variableSelected="variableSelected"
                  />
                </v-tab-item>

                <v-tab-item key="table2" :eager="true">
                  <summary-stats-table
                    :predictor-variables="analysis.predictorVariables"
                    :outcome-variables="analysis.outcomeVariables"
                    :selected-variables="selectedVars"
                    :cohorts="analysis.cohorts"
                    :visible-cohorts="visibleCohorts"
                    :show-category-icons="true"
                    :comparison-field="analysis.input.comparisonField"
                    :is-longitudinal="analysis.collection.is_longitudinal"
                    :analysis="analysis"
                    :anova-pvals="analysis.pvals"
                    :selected-analysis-mode="selectedAnalysisMode"
                    display-mode="table2"
                    @selectedCohorts="updateVisibleCohorts"
                    @variableSelected="variableSelected"
                  />
                </v-tab-item>
              </v-tabs-items>
            </v-sheet>
          </pane>

          <pane size="65" class="pb-1 s_pane">
            <v-sheet color="white" min-height="400px" class="rounded-lg shadow">
              <v-tabs v-model="rightTab2" light>
                <v-tab key="boxplots"> <span>Boxplots/Bar graphs</span> </v-tab>
                <v-tab
                  v-if="
                    getCollectionHasPureToneAudiometryData(analysis.collection)
                  "
                  key="audiogram"
                  >Audiogram</v-tab
                >
              </v-tabs>

              <v-tabs-items v-model="rightTab2">
                <v-tab-item key="boxplots">
                  <v-slider
                    v-model="boxplotsSize"
                    :max="100"
                    style="width: 50%;"
                    class="pl-2 pt-2 ma-0"
                    append-icon="mdi-plus"
                    dense
                  >
                    <template v-slot:prepend
                      ><span class="pr-3 pt-1">Chart&nbsp;height:</span
                      ><v-icon>mdi-minus</v-icon></template
                    >
                  </v-slider>

                  <v-container fluid fill-width class="ma-0 pa-0">
                    <v-row
                      v-for="(detailedView, dvn) in selectedVars"
                      :key="`bp-${analysis.index}-${dvn}`"
                      class="ma-0 pa-0"
                    >
                      <v-col cols="12" class="ma-0 pa-0 export-content">
                        <v-container
                          style="padding: 0; margin-top: 0.5rem; margin-bottom: 0.5rem;"
                        >
                          <v-row class="d-flex justify-center align-center">
                            <!-- title -->
                            <v-col cols="auto" class="text-center pb-0 pt-2">
                              <v-container class="ma-0 pa-0">
                                <v-row class="ma-0 pa-0">
                                  <v-col cols="12" class="ma-0 pa-0">
                                    <v-card-title
                                      class="primary--text pa-0 pl-2"
                                    >
                                      <v-tooltip bottom color="primary">
                                        <template
                                          v-slot:activator="{ on: tooltip }"
                                        >
                                          <img
                                            :src="
                                              '/images/' +
                                                detailedView.category +
                                                '-icon-128.png'
                                            "
                                            :title="detailedView.category"
                                            style="height:1.25em"
                                            class="ma-1"
                                            @error="missingDomainFallback"
                                            v-on="{ ...tooltip }"
                                          />
                                        </template>
                                        <span>{{ detailedView.category }}</span>
                                      </v-tooltip>

                                      <v-tooltip
                                        v-if="detailedView"
                                        top
                                        color="primary"
                                      >
                                        <template
                                          v-slot:activator="{ on: tooltip }"
                                        >
                                          <span
                                            class="text-subtitle-1"
                                            v-on="{ ...tooltip }"
                                          >
                                            {{
                                              detailedView.abbreviation ===
                                              detailedView.label
                                                ? detailedView.abbreviation
                                                : detailedView.abbreviation +
                                                  ' | ' +
                                                  detailedView.label
                                            }}
                                          </span>
                                        </template>
                                        <span>{{
                                          detailedView.description
                                        }}</span>
                                      </v-tooltip>

                                      <span v-else> {{ title }} </span>
                                      <v-spacer />
                                    </v-card-title>
                                  </v-col>
                                </v-row>
                              </v-container>
                            </v-col>
                            <!-- export -->
                            <v-col cols="auto" class="text-center pb-0 pt-2">
                              <export-interface
                                :sec-level-parent="true"
                                :analysis="analysis"
                                :variable="detailedView"
                                export-type="boxplot"
                              />
                            </v-col>
                          </v-row>
                        </v-container>

                        <box-plots
                          v-if="
                            detailedView &&
                              detailedView.data_category == 'Continuous'
                          "
                          ref="boxplots"
                          :analysis="analysis"
                          :cohorts="visibleCohorts"
                          :show-title-bar="false"
                          :outcome-var="detailedView"
                          :max-cohorts="analysis.cohorts.length"
                          :row-height="boxplotRowHeight"
                          :row-pad="boxplotRowPad"
                          :bar-pad="boxplotBarPad"
                          :title-class="boxplotTitleClass"
                          :export-button-size="boxplotExportButtonSize"
                          :min-height="50"
                          :selected-analysis-mode="selectedAnalysisMode"
                        />

                        <stacked-bars
                          v-else
                          ref="stackedbars"
                          :analysis="analysis"
                          :cohorts="visibleCohorts"
                          :show-title-bar="false"
                          :outcome-var="detailedView"
                          :max-cohorts="analysis.cohorts.length"
                          :row-height="boxplotRowHeight"
                          :row-pad="boxplotRowPad"
                          :bar-pad="boxplotBarPad"
                          :title-class="boxplotTitleClass"
                          :export-button-size="boxplotExportButtonSize"
                          :selected-analysis-mode="selectedAnalysisMode"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-tab-item>

                <v-tab-item
                  v-if="
                    getCollectionHasPureToneAudiometryData(analysis.collection)
                  "
                  key="audiogram"
                  :eager="true"
                >
                  <div
                    column
                    align-center
                    justify-center
                    fill-width
                    class="py-3 pl-2"
                  >
                    <audiogram
                      :analysis="analysis"
                      :visible-cohorts="visibleCohorts"
                    />
                  </div>
                </v-tab-item>
              </v-tabs-items>
            </v-sheet>
          </pane>
        </splitpanes>
      </pane>
      <!-- end 2nd row -->
    </splitpanes>
  </v-card>
</template>

<script>
import { Splitpanes, Pane } from 'splitpanes';
import 'splitpanes/dist/splitpanes.css';

import SummaryStatsTable from '@/components/AnalysisSummary/SummaryStatsTable.vue';
import CohortTable from '@/components/common/CohortTable.vue';
import AnalyticsPanel from '@/components/DataExplorer/AnalyticsPanel.vue';
import DetailedView from '@/components/DataExplorer/DetailedView.vue';
import Audiogram from '@/components/DataExplorer/Audiogram.vue';
import BoxPlots from '@/components/AnalysisSummary/BoxPlots.vue';
import StackedBars from '@/components/AnalysisSummary/StackedBars.vue';
import TukeyHsdHeatmap from '@/components/AnalysisSummary/TukeyHSDHeatmap.vue';
import ExportInterface from '@/components/common/ExportInterface.vue';
import ForestPlot from '@/components/AnalysisSummary/ForestPlot.vue';
import logEvent from '@/utils/logging';
import {
  getCollectionHasPureToneAudiometryData,
  missingDomainFallback,
} from '@/utils/helpers';

export default {
  components: {
    AnalyticsPanel,
    Audiogram,
    BoxPlots,
    CohortTable,
    DetailedView,
    StackedBars,
    Splitpanes,
    Pane,
    SummaryStatsTable,
    TukeyHsdHeatmap,
    ExportInterface,
    ForestPlot,
  },
  filters: {
    formatDate(ts) {
      // note that toISOString is going to give us UTC
      return new Date(ts);
    },
  },
  props: {
    title: {
      type: String,
      required: false,
      default: 'Study Groups',
    },
    analysis: {
      type: Object,
      required: true,
      default: () => {},
    },
    multiSelect: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      fullscreen: false,
      expanded: true,
      dialog: false,
      expandAnalytics: true,
      selectedVars: [],
      leftTab1: 1,
      leftTab2: null,
      rightTab1: null,
      rightTab2: null,
      visibleCohorts: [],
      status: 'Submitted',
      error: null,
      statusTime: null,
      statusColor: {
        Submitted: 'primary',
        Running: 'secondary',
        Completed: 'success',
        Failed: 'error',
      },
      pairwiseColorScheme: 'brewer5',
      boxplotsSize: 100,
      boxplotRowHeight: 35,
      boxplotRowPad: 12,
      boxplotBarPad: 5,
      boxplotTitleClass: '',
      boxplotExportButtonSize: 'small',
      getCollectionHasPureToneAudiometryData: getCollectionHasPureToneAudiometryData,
      selectedVarsContainsLongitudinal: false,
      missingDomainFallback: missingDomainFallback,
    };
  },
  computed: {
    selectedAnalysisMode() {
      // note - don't read from state. infer from cohorts in analysis to display UI changes
      if (
        this.analysis.cohorts.length > 0 &&
        this.analysis.cohorts[0].label.startsWith('[O]_')
      ) {
        return 'Outcome based';
      } else {
        return 'Predictor based';
      }
    },
    outOfDate() {
      let generated_epoch = new Date(this.analysis.statusTime).getTime() * 1.0;
      return (
        this.analysis.status == 'Completed' &&
        generated_epoch < this.analysis.collection.last_modified_epoch
      );
    },
    numOriginalCohorts() {
      return this.analysis.input.cohort_ids.split(',').length;
    },
    cohortsMissing() {
      let n_cohorts_orig = this.numOriginalCohorts;
      let n_cohorts_now = this.analysis.cohorts.length;
      return n_cohorts_orig > n_cohorts_now;
    },
  },
  watch: {
    analysis(a) {
      if (a != null) {
        this.visibleCohorts = this.analysis.cohorts;
      }
    },
    'analysis.cohorts': function() {
      // propagate change to child components
      if (this.visibleCohorts.length > 0) {
        this.visibleCohorts.splice(0, 1, this.visibleCohorts[0]);
      }
    },
    leftTab1(lt) {
      logEvent(
        this.$gtag,
        null,
        null,
        'left_tab_changeto_' + lt,
        'analysis',
        'analysis ' + this.analysis.index + ' '
      );
    },
    leftTab2(lt) {
      logEvent(
        this.$gtag,
        null,
        null,
        'left_tab_changeto_' + lt,
        'analysis',
        'analysis ' + this.analysis.index + ' '
      );
    },
    rightTab1(rt) {
      const dv = this.$refs.dview;
      if (dv) dv.map(c => c.onResize());
      window.dispatchEvent(new Event('resize'));

      logEvent(
        this.$gtag,
        null,
        null,
        'right_tab_changeto_' + rt,
        'analysis',
        'analysis ' + this.analysis.index + ' '
      );
    },
    rightTab2(rt) {
      const bp = this.$refs.boxplots;
      const sb = this.$refs.stackedbars;
      setTimeout(async function() {
        if (bp) bp.map(c => c.onResize());
        if (sb) sb.map(c => c.onResize());
      }, 500);

      logEvent(
        this.$gtag,
        null,
        null,
        'right_tab_changeto_' + rt,
        'analysis',
        'analysis ' + this.analysis.index + ' '
      );
    },
    boxplotsSize(bps) {
      var frac = bps / 100.0;
      // original : 35 - 12 - 5
      this.boxplotRowHeight = frac * 20 + 15;
      this.boxplotRowPad = frac * 8 + 4;
      this.boxplotBarPad = frac * 3 + 2;
      this.boxplotExportButtonSize = frac > 0.5 ? 'small' : 'x-small';
      if (frac < 0.2) {
        this.boxplotTitleClass = 'subtitle-2';
      } else if (frac < 0.5) {
        this.boxplotTitleClass = 'subtitle-1';
      } else {
        this.boxplotTitleClass = '';
      }
      this.$nextTick(() => {
        if (this.$refs.boxplots) this.$refs.boxplots.map(c => c.onResize());
        if (this.$refs.stackedbars)
          this.$refs.stackedbars.map(c => c.onResize());
      });
    },
    selectedVars(sv) {
      if (sv.length > 0) {
        this.selectedVarsContainsLongitudinal = sv.some(i =>
          'is_longitudinal' in i ? i.is_longitudinal : false
        );
      } else {
        this.rightTab1 = 0;
        this.selectedVarsContainsLongitudinal = false;
      }
    },
  },
  created() {
    if (this.analysis != null) {
      this.visibleCohorts = this.analysis.cohorts;
    }
  },
  mounted() {
    this.$nextTick(() => {
      const dv = this.$refs.dview;
      if (dv) dv.map(c => c.onResize());

      const bp = this.$refs.boxplots;
      const sb = this.$refs.stackedbars;
      if (bp) bp.map(c => c.onResize());
      if (sb) sb.map(c => c.onResize());
    });
  },
  methods: {
    variableSelected(sv) {
      if (this.selectedAnalysisMode == 'Predictor based') {
        if ('flip_axis' in sv) {
          this.selectedVars = [sv];
        }
      } else {
        this.selectedVars = [sv];
      }
    },
    variablesSelected(nv) {
      if (this.selectedAnalysisMode == 'Predictor based') {
        this.selectedVars = nv.filter(v => 'flip_axis' in v);
      } else {
        this.selectedVars = nv;
      }
    },
    analyticsExpandClicked(nv) {
      this.expandAnalytics = nv;
    },
    updateVisibleCohorts(vc) {
      // keep visibleCohorts sorted in the same order as cohorts
      var vcids = {};
      vc.forEach(c => {
        vcids[c.id] = true;
      });
      this.visibleCohorts = this.analysis.cohorts.filter(c => vcids[c.id]);
    },
    deleteAnalysis() {
      this.$emit('deleteAnalysis');
    },
    getComparisonFieldDescription(cfield) {
      const descrs = {
        change: 'Change from first visit - last visit',
        firstVisit: 'First visit',
        lastVisit: 'Last visit',
      };
      return descrs[cfield];
    },
    splitPane1Resized(evt) {
      var psize = evt[0].size;
      this.expandAnalytics = psize > 30;
      if (this.$refs.dview) {
        this.$refs.dview.map(c => c.onResize());
      }
      window.dispatchEvent(new Event('resize'));
    },
    splitPane2Resized() {
      this.$nextTick(() => {
        if (this.$refs.boxplots) this.$refs.boxplots.map(c => c.onResize());
        if (this.$refs.stackedbars)
          this.$refs.stackedbars.map(c => c.onResize());
      });
      window.dispatchEvent(new Event('resize'));
    },
    updateCohortColor(cc) {
      // propagate event
      this.$emit('cohortColorChange', {
        cohort: cc['cohort'],
        color: cc['color'],
        pattern: cc['pattern'],
      });
    },
    getAnalysisDescription() {
      let dl2descr = {
        left_y_axis: 'First visit',
        right_y_axis: 'Last visit',
        change: 'First - Last visit change',
      };

      // predictor variables used
      let predVars = {};
      this.analysis.cohorts.forEach(c => {
        const queries = c.queries;
        queries.forEach(q => {
          let dl_lbl = q.input_variable.dimension_label
            ? ' - ' + dl2descr[q.input_variable.dimension_label]
            : '';
          if ('subject_ontology' in q.input_variable) {
            predVars[q.input_variable.subject_ontology.label + dl_lbl] = 1;
          } else if ('observation_ontology' in q.input_variable) {
            predVars[q.input_variable.observation_ontology.label + dl_lbl] = 1;
          }
        });
      });
      if (this.selectedAnalysisMode == 'Predictor based') {
        return 'Predictors: ' + Object.keys(predVars).join(' and ');
      } else {
        return 'Outcomes: ' + Object.keys(predVars).join(' and ');
      }
    },
  },
};
</script>
<style>
table.v-data-table thead tr th {
  font-size: 24px;
}

table.v-data-table tbody tr td {
  font-size: 24px;
}

div.s_pane {
  background-color: white !important;
  overflow-y: auto;
}
</style>
